import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/* ================= TYPES ================= */

// ✅ What is STORED in Redux
export type CartItem = {
  listingId: string;
  brandId: string;   // 🔥 REQUIRED for delivery
  hostId: string;
  name: string;
  price: number;
  photo?: string;
  qty: number;
  colorId?: number;
  sizeId?: number;
};

// ✅ What UI SENDS to addToCart
export type AddToCartPayload = {
  listingId: string;
  brandId: string;   // 🔥 REQUIRED
  hostId: string;
  name: string;
  price: number;
  photo?: string;
  qty?: number;
  colorId?: number;
  sizeId?: number;
};

export type VariantKey = {
  listingId: string;
  colorId?: number;
  sizeId?: number;
};

export type CartState = {
  userId: string | null;
  items: CartItem[];
};

/* ================= STATE ================= */

const initialState: CartState = {
  userId: null,
  items: [],
};

/* ================= SLICE ================= */

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setCartUser: (state, action: PayloadAction<string>) => {
      state.userId = action.payload;
    },

    /* ===== ADD TO CART ===== */
    addToCart: (
      state,
      action: PayloadAction<AddToCartPayload>
    ) => {
      const qtyToAdd = action.payload.qty ?? 1;

      const existing = state.items.find(
        (i) =>
          i.listingId === action.payload.listingId &&
          i.colorId === action.payload.colorId &&
          i.sizeId === action.payload.sizeId
      );

      if (existing) {
        existing.qty += qtyToAdd;
      } else {
        state.items.push({
          ...action.payload,   // 🔥 This already includes brandId
          qty: qtyToAdd,
        });
      }
    },

    /* ===== DECREMENT ===== */
    decrementCartItem: (
      state,
      action: PayloadAction<VariantKey>
    ) => {
      const item = state.items.find(
        (i) =>
          i.listingId === action.payload.listingId &&
          i.colorId === action.payload.colorId &&
          i.sizeId === action.payload.sizeId
      );

      if (!item) return;

      if (item.qty > 1) {
        item.qty -= 1;
      } else {
        state.items = state.items.filter(
          (i) =>
            !(
              i.listingId === action.payload.listingId &&
              i.colorId === action.payload.colorId &&
              i.sizeId === action.payload.sizeId
            )
        );
      }
    },

    /* ===== REMOVE ===== */
    removeFromCart: (
      state,
      action: PayloadAction<VariantKey>
    ) => {
      state.items = state.items.filter(
        (i) =>
          !(
            i.listingId === action.payload.listingId &&
            i.colorId === action.payload.colorId &&
            i.sizeId === action.payload.sizeId
          )
      );
    },

    clearCart: (state) => {
      state.items = [];
    },
  },
});

export const {
  setCartUser,
  addToCart,
  decrementCartItem,
  removeFromCart,
  clearCart,
} = cartSlice.actions;

export default cartSlice.reducer;
